import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import * as stylex from "@stylexjs/stylex";
import type { ReactElement, ReactNode } from "react";
import { useIsMobile } from "../hooks/useMediaQuery.ts";
import { CloseIcon } from "../icons.ts";
import "../tokens.stylex.ts";
import { color, fontSize, fontWeight, lineHeight, media, motion, radius, space, zIndex } from "../tokens.stylex.ts";
import { Button } from "./Button.tsx";
import { isTransitioning } from "./overlay.styles.ts";
import { Sheet } from "./Sheet.tsx";
import { surface } from "./surface.styles.ts";
export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog, e.g. `<Button>New channel</Button>`. */
  trigger?: ReactElement;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer actions, right-aligned. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

/** Centered modal on larger screens; a swipeable bottom sheet on phones. */
export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = "md"
}: DialogProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <Sheet open={open} onOpenChange={onOpenChange} trigger={trigger} title={title} description={description} footer={footer}>
        {children}
      </Sheet>;
  }
  return <BaseDialog.Root open={open} onOpenChange={onOpenChange ? next => onOpenChange(next) : undefined}>
      {trigger ? <BaseDialog.Trigger render={trigger} /> : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={state => ({
        0: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni xjx9puj"
        },
        1: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni xjx9puj xg01cxk"
        }
      })[!!isTransitioning(state.transitionStatus) << 0].className} />
        <BaseDialog.Popup className={state => stylex.props(surface.raised, surface.lg, styles.popup, sizes[size], isTransitioning(state.transitionStatus) && styles.popupHidden).className}>
          <div className="x78zum5 x1cy8zhl x1qughib xpcbtbh x156km83 xsb4d8f x1l9m7az">
            <div className="x78zum5 xdt5ytf xjenv9e xeuugli">
              <BaseDialog.Title className="x1ghz6dp x1p23z6u x1ivwu1z x9aqtqa">{title}</BaseDialog.Title>
              {description ? <BaseDialog.Description className="x1ghz6dp xluwven x187ovcu x9tub0y">{description}</BaseDialog.Description> : null}
            </div>
            <BaseDialog.Close render={<Button variant="ghost" size="sm" iconOnly icon={<CloseIcon size={16} />} aria-label="Close" />} />
          </div>
          {children ? <div className="x2lwn1j xsb4d8f x1q13efz x1odjw0f">{children}</div> : null}
          {footer ? <div className="x78zum5 x1q0g3np x1g0459a xsvlhvi x13a6bvl x1a62zme xh6stid x1l9m7az x1nmxbqc xsb4d8f x178xt8z x13fuv20 x8yrp0t x5rcijh xfcbtqh xvccuex">{footer}</div> : null}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>;
}
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  pending?: boolean;
  /** Destructive actions get the danger treatment. */
  destructive?: boolean;
  children?: ReactNode;
  confirmDisabled?: boolean;
}

/**
 * For irreversible actions (revoke, delete). Uses `alertdialog` semantics.
 * On phones it docks to the bottom edge like a native action sheet.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  pending,
  destructive = true,
  children,
  confirmDisabled
}: ConfirmDialogProps) {
  return <AlertDialog.Root open={open} onOpenChange={next => onOpenChange(next)}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={state => ({
        0: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni xjx9puj"
        },
        1: {
          className: "xixxii4 x10a8y8t x42wk67 x1bmlxz2 x19991ni xjx9puj xg01cxk"
        }
      })[!!isTransitioning(state.transitionStatus) << 0].className} />
        <AlertDialog.Popup className={state => stylex.props(surface.raised, surface.lg, styles.popup, sizes.sm, isTransitioning(state.transitionStatus) && styles.popupHidden).className}>
          <div className="x78zum5 x1cy8zhl x1qughib xpcbtbh x156km83 xsb4d8f x1l9m7az">
            <div className="x78zum5 xdt5ytf xjenv9e xeuugli">
              <AlertDialog.Title className="x1ghz6dp x1p23z6u x1ivwu1z x9aqtqa">{title}</AlertDialog.Title>
              <AlertDialog.Description className="x1ghz6dp xluwven x187ovcu x9tub0y">{description}</AlertDialog.Description>
            </div>
          </div>
          {children ? <div className="x2lwn1j xsb4d8f x1q13efz x1odjw0f">{children}</div> : null}
          <div className="x78zum5 x1q0g3np x1g0459a xsvlhvi x13a6bvl x1a62zme xh6stid x1l9m7az x1nmxbqc xsb4d8f x178xt8z x13fuv20 x8yrp0t x5rcijh xfcbtqh xvccuex">
            <AlertDialog.Close render={<Button variant="secondary">Cancel</Button>} />
            <Button variant={destructive ? "danger" : "primary"} loading={pending} disabled={confirmDisabled} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>;
}
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";
const styles = {
  popup: {
    kVAEAm: "xixxii4",
    k87sOh: "x1ks3263 xyoajkv",
    krVfgx: "x112czck",
    kbCHJM: "x1nrll8i x1drdrr2",
    k3aq6I: "xuuh30 xavrw21",
    kY2c9j: "xlj7wz5",
    kzqmXN: "x1cf4oau x1dcy9r8",
    kskxy: "x2g0g5p x5gbpff",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kaIpWk: "x16l8lxw xwjeq5b",
    kI3sdo: "x1a2a7pz",
    k1ekBW: "x6o7n8i",
    kIyJzY: "xjx9puj x19v5kzs",
    kAMwcw: "xyda15a x1vdto58",
    $$css: true
  },
  popupHidden: {
    kSiTet: "xg01cxk x1qv7yzy",
    k3aq6I: "xg9fqp8 x1o9bbq6",
    $$css: true
  }
};
const sizes = {
  sm: {
    ks0D6T: "x1lr1uin xcjaj2c",
    $$css: true
  },
  md: {
    ks0D6T: "x1lwm3bp xcjaj2c",
    $$css: true
  },
  lg: {
    ks0D6T: "x7ep2pv xcjaj2c",
    $$css: true
  }
};